import { PaymentType, PaymentVendorType, PromotionType } from '@server/schemas'
import { FEATURE_FLAG_KEYS } from '@shared/constants'
import { match } from 'ts-pattern'

import { Typography, VStack } from '@podo-app/design-system-temp'
import { useFlagsCompat } from '@podo/flags/react'

type Props = {
  paymentType: PaymentType
  promotionType?: PromotionType | null
  isSmartTalk: boolean
  paymentVendorType: PaymentVendorType
  /** 튜터 지정권 결제 여부 — true면 일반 패키지가 아닌 지정권 전용 약관 플래그를 사용한다. */
  isTutorConnect?: boolean
  /** 튜터 지정권 상품의 판매 국가 — JP 상품은 일본 전용 약관 플래그를 사용한다. */
  tutorConnectCountryCode?: string | null
}

type PaymentTerm = {
  type: 'REQUIRED' | 'OPTIONAL'
  title: string
  contents: string[]
}

const PaymentTerms = ({
  paymentType,
  promotionType,
  isSmartTalk,
  paymentVendorType,
  isTutorConnect,
  tutorConnectCountryCode,
}: Props) => {
  const {
    subscribe_term_messages: subscribeTermMessages,
    lump_sum_term_messages: lumpSumTermMessages,
    terms_payback_messages: termsPaybackMessages,
    ipad_term_messages: ipadTermMessages,
    business_term_messages: businessTermMessages,
    smart_talk_term_messages: smartTalkTermMessages,
    smart_talk_trial_term_messages: smartTalkTrialTermMessages,
    naver_pay_term_messages: naverPayTermMessages,
    tutor_connect_term_messages: tutorConnectTermMessages,
    tutor_connect_term_messages_jp: tutorConnectTermMessagesJp,
  } = useFlagsCompat([
    FEATURE_FLAG_KEYS.SUBSCRIBE_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.LUMP_SUM_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.TERMS_PAYBACK_MESSAGES,
    FEATURE_FLAG_KEYS.IPAD_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.BUSINESS_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.SMART_TALK_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.SMART_TALK_TRIAL_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.NAVER_PAY_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.TUTOR_CONNECT_TERM_MESSAGES,
    FEATURE_FLAG_KEYS.TUTOR_CONNECT_TERM_MESSAGES_JP,
  ])

  const parsePaymentTerm = (flagValue: string | number | boolean | null | undefined): PaymentTerm[] => {
    if (typeof flagValue !== 'string' || !flagValue) {
      return []
    }
    try {
      return JSON.parse(flagValue) as PaymentTerm[]
    } catch (e) {
      console.error('Error parsing payment term from feature flag', e)
      return []
    }
  }

  const paymentTermsByPaymentType = match({
    paymentType,
    isSmartTalk,
    promotionType,
    isTutorConnect,
    tutorConnectCountryCode,
  })
    .returnType<PaymentTerm[]>()
    // 튜터 지정권은 패키지(LUMP_SUM)와 별개의 약관을 쓰므로 최우선으로 분기한다.
    .with({ isTutorConnect: true, tutorConnectCountryCode: 'JP' }, () =>
      parsePaymentTerm(tutorConnectTermMessagesJp.value),
    )
    .with({ isTutorConnect: true }, () => parsePaymentTerm(tutorConnectTermMessages.value))
    .with({ isSmartTalk: true, paymentType: 'TRIAL' }, () => parsePaymentTerm(smartTalkTrialTermMessages.value))
    .with({ isSmartTalk: true }, () => parsePaymentTerm(smartTalkTermMessages.value))
    .with({ promotionType: 'PAYBACK' }, () => parsePaymentTerm(termsPaybackMessages.value))
    .otherwise(({ paymentType }) =>
      match(paymentType)
        .returnType<PaymentTerm[]>()
        .with('SUBSCRIBE', () => parsePaymentTerm(subscribeTermMessages.value))
        .with('LUMP_SUM', () => parsePaymentTerm(lumpSumTermMessages.value))
        .with('IPAD', () => parsePaymentTerm(ipadTermMessages.value))
        .with('BUSINESS', () => parsePaymentTerm(businessTermMessages.value))
        .with('TRIAL', () => [])
        .with('EXTEND', () => [])
        .otherwise(() => []),
    )

  return (
    <VStack className="bg-gray-100 px-5 py-8 gap-4">
      {paymentTermsByPaymentType.map((term) => {
        return (
          <VStack className="gap-1.5" key={term.title}>
            <Typography size="h6" className="text-gray-400">
              {term.title}
            </Typography>

            <ol className="gap-0.5 list-disc list-outside pl-5 marker:text-gray-400">
              {term.contents.map((content) => (
                <li key={content}>
                  <Typography size="body2" className="text-gray-400 font-[400]">
                    {content}
                  </Typography>
                </li>
              ))}
            </ol>
          </VStack>
        )
      })}
      {/* Npay 결제수단안내 */}
      {paymentVendorType === 'NAVER' &&
        parsePaymentTerm(naverPayTermMessages.value).map((term) => {
          return (
            <VStack className="gap-1.5" key={term.title}>
              <Typography size="h6" className="text-gray-400">
                {term.title}
              </Typography>

              <ol className="gap-0.5 list-disc list-outside pl-5 marker:text-gray-400">
                {term.contents.map((content) => (
                  <li key={content}>
                    <Typography size="body2" className="text-gray-400 font-[400]">
                      {content}
                    </Typography>
                  </li>
                ))}
              </ol>
            </VStack>
          )
        })}
    </VStack>
  )
}

export default PaymentTerms
